import { Suspense } from 'react';
import { ErrorBoundary } from './error-boundary';
import { useComponentExtension, useComponentExtensions } from './extension-hooks';

// This package can't import from @fox/atoms - it is a circular dependency
// Given we have minimal requirements we can just import the mui skeleton directly
import MUISkeleton from '@mui/material/Skeleton';

function isLegacyExtension<T, TName extends string>(props: ExtensionPropsBase<TName>): props is LegacyExtensionProps<T, TName> {
	if (typeof (props as any).props === 'object') return true;
	return false;
}

export type ExtensionPropsBase<TName extends string = string> = {
	/** the name of the extension point */
	name: TName;

	/** Allow extensions to be rendered if we are not authenticated */
	anonymous?: boolean;
};

type RootExtensionProps<T, TName extends string = string> = ExtensionPropsBase<TName> & T;
type LegacyExtensionProps<T, TName extends string = string> = ExtensionPropsBase<TName> & { props: T };
type ExtensionProps<T, TName extends string = string> = LegacyExtensionProps<T, TName> | RootExtensionProps<T, TName>;

export function Extensions<T extends {}, TName extends string = string>({
	name,
	props
}: Omit<ExtensionPropsBase<TName> & { props?: T }, 'children'>) {
	const components = useComponentExtensions(name);

	return (
		<>
			{components.map((ExtensionComponent, i) => (
				<ErrorBoundary key={i}>
					<ExtensionComponent {...props} />
				</ErrorBoundary>
			))}
		</>
	);
}

/** The placeholder / extension component that exposes pluggable functionality to the system. This is designed to have a single extension loaded in here
 * with the children being the fallback content
 */
export function Extension<T extends {}, TName extends string = string>(props: React.PropsWithChildren<{}> & ExtensionProps<T, TName>) {
	const { name, anonymous, children, ...rest } = props;
	const ExtensionComponent = useComponentExtension<TName>(name, anonymous);

	const { props: _, ...others } = rest as any;

	const extensionProps = isLegacyExtension<T, TName>(props) ? { ...others, ...props.props } : rest;

	if (ExtensionComponent) {
		return (
			<ErrorBoundary>
				{/* We include a suspense boundary here to allow async loading of components. Given this could be used on a table element we don't want to spin here */}
				<Suspense fallback={<MUISkeleton sx={{ transform: 'none' }} />}>
					<ExtensionComponent {...extensionProps} />
					{/* 
					  this code can be used to highlight what extensions are where
					  (deliberate spaces here to highlight this comment in the code)
					<div style={{border: '1px solid #' + intToRGB(hashCode(name))}}>
						<div style={{position: 'relative', lineHeight: "10px", zIndex: 999, fontSize: 10}}>ext: {name}
						</div>
						<ExtensionComponent {...props} />
					</div> */}
				</Suspense>
			</ErrorBoundary>
		);
	}

	if (children) return <>{children}</>;

	return null;
}
